import AppLayout from "./layout/AppLayout";
import AuthLayout from "./layout/AuthLayout";
import ForgotPassword from "./pages/AuthPages/ForgotPassword";
import NotFound from "./pages/OtherPage/NotFound";
import SignIn from "./pages/AuthPages/SignIn";
import SignUp from "./pages/AuthPages/SignUp";
import SignupSuccess from "./pages/AuthPages/SignupSuccess";
import UpdatePassword from "./pages/AuthPages/UpdatePassword";
import Upload from './pages/Upload';
import UserDashboard from "./pages/Dashboard/UserDashboard";
import { userStateService } from "./services/UserStateService";
import { ProtectedRoute } from './components/ProtectedRoute';
import { Route, BrowserRouter as Router, Routes } from "react-router";
import { supabase } from "./integrations/supabase/client";
import { useEffect, useState } from 'react';
import Settings from "./pages/Settings"; // Add this import at the top with other imports
import FilesTable from "./pages/FilesTable";
import EditPersonalProfile from './pages/EditPersonalProfile';
import Subscription from './pages/Subscription';

export default function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [sessionExpired, setSessionExpired] = useState(false);
  const [isLoading, setIsLoading] = useState(true); // Add loading state

  useEffect(() => {
    // Check initial auth state
    const checkAuth = async () => {
      setIsLoading(true);
      
      try {
        const { data: { session } } = await supabase.auth.getSession();
        
        if (session) {
          const expiresAt = session.expires_at! * 1000; // Convert to milliseconds
          const now = Date.now();
          const isExpired = now >= expiresAt;

          setSessionExpired(isExpired);
          setIsAuthenticated(!isExpired && !!session);

          // Optional: Log expiry information
          // console.log('Session expires at:', new Date(expiresAt));
          // console.log('Current time:', new Date(now));
          // console.log('Is session expired:', isExpired);

          if (!isExpired) {
            userStateService.setUserInfo(
              session.user?.id || '',
              session.user?.user_metadata?.name || 'User',
              session.user?.email || ''
            );
          }
        } else {
          setIsAuthenticated(false);
          setSessionExpired(false);
        }
      } catch (error) {
        console.error('Authentication check error:', error);
        setIsAuthenticated(false);
        setSessionExpired(false);
      } finally {
        setIsLoading(false);
      }
    };

    checkAuth();

    // Listen for auth changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        const expiresAt = session.expires_at! * 1000;
        const now = Date.now();
        const isExpired = now >= expiresAt;

        setSessionExpired(isExpired);
        setIsAuthenticated(!isExpired && !!session);

        userStateService.setUserInfo( 
          session.user?.id || '',
          session.user?.user_metadata?.name || 'User',
          session.user?.email || ''
        );

      } else {
        setIsAuthenticated(false);
        setSessionExpired(false);
      }
      setIsLoading(false);
    });

    // const channels = supabase.channel('custom-all-channel')
    //   .on(
    //     'postgres_changes',
    //     { event: '*', schema: 'public', table: 'tiers' },
    //     (payload) => {
    //       console.log('Tiers Change received!', payload)
    //     }
    //   )
    //   .subscribe();

    // const channels2 = supabase.channel('custom-all-channel2')
    //   .on(
    //     'postgres_changes',
    //     { event: '*', schema: 'public', table: 'profiles' },
    //     (payload) => {
    //       console.log('Profiles Change received! ', payload)
    //     }
    //   )
    //   .subscribe();

    return () => subscription.unsubscribe();
  }, []);

  // Optional: Add a useEffect to check expiry periodically
  useEffect(() => {
    const checkSession = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (session) {
        const expiresAt = session.expires_at! * 1000;
        const now = Date.now();
        const isExpired = now >= expiresAt;

        if (isExpired) {
          setSessionExpired(true);
          setIsAuthenticated(false);
          // Optional: Redirect to login
          // window.location.href = '/signin';
        }
      }
    };

    const interval = setInterval(checkSession, 60000); // Check every minute
    return () => clearInterval(interval);
  }, []);

  // Loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100 dark:bg-gray-900">
        <div className="p-4 bg-white dark:bg-gray-800 rounded shadow-md">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 border-4 border-t-blue-500 border-b-gray-200 border-l-gray-200 border-r-gray-200 rounded-full animate-spin dark:border-b-gray-700 dark:border-l-gray-700 dark:border-r-gray-700"></div>
            <p className="text-gray-700 dark:text-gray-300">Loading application...</p>
          </div>
        </div>
      </div>
    );
  }

  // You can use sessionExpired state to show a message or redirect
  if (sessionExpired) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-100">
        <div className="p-4 bg-white rounded shadow-md">
          <h2 className="text-xl font-semibold text-red-600">Session Expired</h2>
          <p className="mt-2 text-gray-600">Please sign in again to continue.</p>
          <button
            onClick={() => window.location.href = '/signin'}
            className="mt-4 px-4 py-2 bg-brand-500 text-white rounded hover:bg-brand-600"
          >
            Sign In
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      <Router>
        <Routes>
          {/* Auth Layout - Public Routes */}
          <Route element={<AuthLayout />}>
            <Route path="/signin" element={<SignIn />} />
            <Route path="/signup" element={<SignUp />} />
            <Route path="/signup-success" element={<SignupSuccess />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/update-password" element={<UpdatePassword />} />
          </Route>

          {/* Protected Routes */}
          <Route element={<ProtectedRoute isAuthenticated={isAuthenticated} />}>
            <Route element={<AppLayout />}>
              <Route index path="/" element={<UserDashboard />} />
              {/* <Route path="/ecommerce" element={<Ecommerce />} /> */}
              <Route path="/convert" element={<Upload />} />
              <Route path="/files" element={<FilesTable />} />
              <Route path="/profile" element={<EditPersonalProfile />} />
              <Route path="/settings" element={<Settings />} />
              <Route path="/subscription" element={<Subscription />} />

              {/* <Route path="/tiers" element={<Tiers />} /> */}
              {/* <Route path="/tiersx" element={<TiersTableX />} />
              <Route path="/user-profiles" element={<UserProfiles />} />
              <Route path="/calendar" element={<Calendar />} />
              <Route path="/blank" element={<Blank />} />  */}

              {/* Forms */}
              {/* <Route path="/form-elements" element={<FormElements />} /> */}

              {/* Tables */}
              {/* <Route path="/basic-tables" element={<BasicTables />} /> */}

              {/* Ui Elements */}
              {/* <Route path="/alerts" element={<Alerts />} />
              <Route path="/avatars" element={<Avatars />} />
              <Route path="/badges" element={<Badges />} />
              <Route path="/buttons" element={<Buttons />} />
              <Route path="/images" element={<Images />} />
              <Route path="/videos" element={<Videos />} /> */}
            </Route>
          </Route>

          <Route path="*" element={<NotFound />} />
        </Routes>
      </Router>
    </>
  );
}
